import * as React from 'react';

import {GenericFormConsumerInterface, GenericReactProviderFormInterfaceOutput, useForm,} from '../../hooks';

import {DataMutationLoginAsyncProvider, useDataMutationLoginAsync,} from '../data-mutation-login-async';

import {LoggedInMagentoUiProvider, useLoggedInMagentoUi,} from '../logged-in-magento-ui-provider';

import {JoinForm} from '../join-form-provider';

import {useLoginApp} from '../login-app-provider';
import {PRESENTATION_SCROLL_ACTIONS, usePresenterForScroll_asController,} from '../presenter-for-scroll-provider';
import {useProminentError} from '../prominent-error-provider';

import {LoginForm, LoginFormApp, LoginFormInitialState, LoginFormLogic,} from './login-form';

interface LoginFormConsumerInterface
  extends GenericFormConsumerInterface<LoginForm> {
  login: () => void;
  isLoading: boolean;
  isLoggedIn: boolean;
}
const defaultValue = null;
const LoginFormContext = React.createContext<LoginFormConsumerInterface | null>(
  defaultValue
);

export interface LoginForm_ControllerConsumerInterface {
  loginWithJoinForm: (joinForm: JoinForm) => Promise<void>;
  loginWithOneTimePassword: (
    email: string,
    oneTimePassword: string
  ) => Promise<void>;
  injectPasswordFieldError: (message: string) => void;
  resetLoginForm: () => void;
}
const defaultControllerValue =
  {} as unknown as LoginForm_ControllerConsumerInterface;
const LoginFormConrollerContext = React.createContext(defaultControllerValue);

interface LoginPostResponseJson {
  rendered_error_message_html?: string;
}

export enum LOGIN_FORM_WINDOW_EVENTS {
  LOGIN_FAILED = "LOGIN_FAILED",
  LOGIN_SUCCESS = "LOGIN_SUCCESS",
}

export interface InitialStateMinimumRequirements_LoginPageWithFormKey {
  form_key?: string;
  login_form_action?: string;
}

interface LoginFormProviderProps {
  useInitialState: () => {
    initialState: LoginFormApp["initialState"] &
      InitialStateMinimumRequirements_LoginPageWithFormKey;
  };
}
const ActualLoginFormProvider: React.FunctionComponent<
  LoginFormProviderProps
> = (props) => {
  const {
    shouldLoginUsingTheSpecialAsynchronousApiInsteadOfFormPost,
    shouldContinueLoadingAfterLoginSuccess,
  } = useLoginApp();

  const { initialState } = props.useInitialState();
  const { isCustomerLoggedIn } = initialState || {};

  const { setProminentError } = useProminentError();
  const { initiateScrollAction } = usePresenterForScroll_asController();

  const { mutateAsync } = useDataMutationLoginAsync();

  const { presentLoggedInMagentoUi } = useLoggedInMagentoUi();

  const [isLoading, setIsLoading] = React.useState(false);
  const [hasLoggedInUsingThis, setHasLoggedInUsingThis] = React.useState(false);

  const isLoggedIn = React.useMemo(() => {
    return isCustomerLoggedIn || hasLoggedInUsingThis;
  }, [isCustomerLoggedIn, hasLoggedInUsingThis]);

  const thisForm = useForm<LoginForm, LoginFormApp>(
    LoginFormLogic,
    () => ({
      initialState,
    }),
    LoginFormInitialState
  );
  const { form, validateRightNowAndGiveMeAYesOrNo } = thisForm;

  const loginApi = React.useCallback(
    async (loginForm: LoginForm, oneTimePassword?: string) => {
      if (shouldLoginUsingTheSpecialAsynchronousApiInsteadOfFormPost) {
        let loginRecaptcha;
        if (initialState.login_form_recaptcha.recaptchaEnabled) {
          const captchaWrapperId =
            initialState.login_form_recaptcha.recaptchaId + "-react-wrapper";
          let recaptchaInput = document.querySelector(
            "form.bbnt-common-login-form-ui > .input-container.recaptcha-input.named-recaptcha .g-recaptcha-response"
          ) as HTMLTextAreaElement;
          recaptchaInput =
            recaptchaInput ||
            document
              .querySelector("#" + captchaWrapperId)
              ?.querySelector(".g-recaptcha-response");

          if (recaptchaInput) {
            loginRecaptcha = recaptchaInput.value;
          }
        }

        await mutateAsync({
          loginForm,
          loginRecaptcha,
          oneTimePassword,
        });

        setHasLoggedInUsingThis(true);

        await presentLoggedInMagentoUi();

        (window as any).dataLayer = Array.isArray((window as any).dataLayer)
          ? (window as any).dataLayer
          : [];

        (window as any).dataLayer.push({
          event: "login",
          method: "UsernamePassword",
          email: loginForm.email,
          time: new Date().toString(),
        });

        window.dispatchEvent(new Event(LOGIN_FORM_WINDOW_EVENTS.LOGIN_SUCCESS));
      } else {
        const formKey = initialState.form_key;

        const formData = new FormData();
        formData.append("form_key", formKey || "");
        formData.append("login[username]", loginForm.email || "");
        formData.append("login[password]", loginForm.password || "");
        if (oneTimePassword) {
          formData.append("login[otpcode]", oneTimePassword);
        }
        if (initialState.login_form_recaptcha.recaptchaEnabled) {
          const captchaWrapperId =
            initialState.login_form_recaptcha.recaptchaId + "-react-wrapper";
          let recaptchaInput = document.querySelector(
            "form.bbnt-common-login-form-ui > .input-container.recaptcha-input.named-recaptcha .g-recaptcha-response"
          ) as HTMLTextAreaElement;
          recaptchaInput =
            recaptchaInput ||
            document
              .querySelector("#" + captchaWrapperId)
              ?.querySelector(".g-recaptcha-response");

          if (recaptchaInput) {
            formData.append("g-recaptcha-response", recaptchaInput.value);
          }
        }

        if (!initialState.login_form_action) {
          throw new Error("No login form action.");
        }

        const headers = new Headers();
        if (initialState.login_form_recaptcha.recaptchaEnabled) {
          const captchaWrapperId =
            initialState.login_form_recaptcha.recaptchaId + "-react-wrapper";
          let recaptchaInput = document.querySelector(
            "form.bbnt-common-login-form-ui > .input-container.recaptcha-input.named-recaptcha .g-recaptcha-response"
          ) as HTMLTextAreaElement;
          recaptchaInput =
            recaptchaInput ||
            document
              .querySelector("#" + captchaWrapperId)
              ?.querySelector(".g-recaptcha-response");
          if (recaptchaInput) {
            headers.append("X-ReCaptcha", recaptchaInput.value);
          }
        }

        const request = new Request(initialState.login_form_action, {
          method: "POST",
          body: formData,
          headers,
        });

        const loginResponse = await fetch(request);

        if (loginResponse.redirected) {
          setHasLoggedInUsingThis(true);

          (window as any).dataLayer = Array.isArray((window as any).dataLayer)
            ? (window as any).dataLayer
            : [];

          (window as any).dataLayer.push({
            event: "login",
            method: "UsernamePassword",
            email: loginForm.email,
            time: new Date().toString(),
          });

          await presentLoggedInMagentoUi();

          window.dispatchEvent(
            new Event(LOGIN_FORM_WINDOW_EVENTS.LOGIN_SUCCESS)
          );
          window.location.href = loginResponse.url;
        } else {
          let errorMessage =
            "Your email or password is incorrect. Please enter the correct details or Join For Free.";

          const data: LoginPostResponseJson = await loginResponse.json();

          if (data.rendered_error_message_html) {
            errorMessage = data.rendered_error_message_html;
          }

          throw new Error(errorMessage);
        }
      }
    },
    [
      shouldLoginUsingTheSpecialAsynchronousApiInsteadOfFormPost,
      initialState,
      setHasLoggedInUsingThis,
      presentLoggedInMagentoUi,
    ]
  );

  const loginWithLoginForm = React.useCallback(async () => {
    try {
      setIsLoading(true);

      await loginApi(form);

      if (!shouldContinueLoadingAfterLoginSuccess) {
        setIsLoading(false);
      } else {
        /**
         * No need to set the loading off ->
         * page will be in the middle of redirecting if all goes well,
         * if anything has gone wrong, it will be handled by the catch block
         */
        // setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);

      let errorMessage = "An unknown error has occurred.";
      if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }

      setProminentError(errorMessage);

      window.dispatchEvent(new Event(LOGIN_FORM_WINDOW_EVENTS.LOGIN_FAILED));
    }
  }, [
    shouldLoginUsingTheSpecialAsynchronousApiInsteadOfFormPost,
    shouldContinueLoadingAfterLoginSuccess,
    setIsLoading,
    loginApi,
    form,
    setProminentError,
  ]);

  const login = React.useCallback(() => {
    const isValid = validateRightNowAndGiveMeAYesOrNo();
    if (isValid) {
      loginWithLoginForm();
    } else {
      setProminentError(
        "Some information is missing or invalid. Check the red fields above for details."
      );
      initiateScrollAction(
        PRESENTATION_SCROLL_ACTIONS.SCROLL_TO_FORM_ERROR_FIELD
      );
    }
  }, [
    validateRightNowAndGiveMeAYesOrNo,
    loginWithLoginForm,
    setProminentError,
    initiateScrollAction,
  ]);

  const loginWithJoinForm = React.useCallback(
    async (joinForm: JoinForm) => {
      await loginApi({
        email: joinForm.emailAddress,
        password: joinForm.password,
      });
    },
    [loginApi]
  );

  const loginWithOneTimePassword: LoginForm_ControllerConsumerInterface["loginWithOneTimePassword"] =
    React.useCallback(
      async (email, oneTimePassword) => {
        return await loginApi(
          {
            email,
          },
          oneTimePassword
        );
      },
      [loginApi]
    );

  const injectPasswordFieldError: LoginForm_ControllerConsumerInterface["injectPasswordFieldError"] =
    React.useCallback(
      (message: string) => {
        thisForm.inject({
          passwordError: message,
        });
      },
      [thisForm]
    );

  const value: LoginFormConsumerInterface = React.useMemo(
    () => ({
      ...GenericReactProviderFormInterfaceOutput<LoginForm, LoginFormApp>(
        thisForm
      ),

      login,
      isLoading,
      isLoggedIn,
    }),
    [thisForm, login, isLoading, isLoggedIn]
  );

  const controllerValue: LoginForm_ControllerConsumerInterface = React.useMemo(
    () => ({
      loginWithJoinForm,
      loginWithOneTimePassword,
      injectPasswordFieldError,
      resetLoginForm: thisForm.resetForm,
    }),
    [
      loginWithJoinForm,
      loginWithOneTimePassword,
      injectPasswordFieldError,
      thisForm,
    ]
  );

  return (
    <LoginFormContext.Provider value={value}>
      <LoginFormConrollerContext.Provider value={controllerValue}>
        {props.children}
      </LoginFormConrollerContext.Provider>
    </LoginFormContext.Provider>
  );
};

export const LoginFormProvider: React.FunctionComponent<
  LoginFormProviderProps
> = (props) => {
  return (
    <LoggedInMagentoUiProvider>
      <DataMutationLoginAsyncProvider>
        <ActualLoginFormProvider {...props}>
          {props.children || []}
        </ActualLoginFormProvider>
      </DataMutationLoginAsyncProvider>
    </LoggedInMagentoUiProvider>
  );
};

export const useLoginForm = () => {
  const context = React.useContext(LoginFormContext);
  if (context === defaultValue) {
    throw new Error("useLoginForm must be used within a LoginFormProvider");
  }
  return context;
};

export const useLoginForm_asController = () => {
  const context = React.useContext(LoginFormConrollerContext);
  if (context === defaultControllerValue) {
    throw new Error(
      "useLoginForm_asController must be used within a LoginFormProvider"
    );
  }
  return context;
};
